<template>
  <UiMenu
    v-model:query="queryModel"
    :class="variant === 'list' ? 'w-full' : 'w-fit'"
    :default-title="label"
    :multi-label="label"
    list-variant="checkbox"
    icon-size="md"
    :has-search="hasSearch"
    :search-placeholder="t('basic.search')"
    :has-auto-focus="hasSearch"
    :has-reset-option="hasSearch"
    :reset-label="t('basic.reset')"
    :button-appearance="buttonAppearance"
    :options="options"
    @open="handleOpen"
    @close="handleClose"
    @reset="handleReset"
    @option-selected="onOptionSelected">
    <template #default="slotProps">
      <slot name="default" v-bind="slotProps" />
    </template>
  </UiMenu>
</template>

<script setup lang="ts">
import UiMenu, {
  type UiMenuOptionType,
  type UiMenuSelectedOption,
} from "@dpa-id-components/dpa-shared-components/src/components/UiMenu/UiMenu.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { useTracking } from "@/composition/shared/useTracking.ts";
import useSavedSearchesStore from "@/stores/search/savedSearches.ts";

import type { FilterSelectorItemWrapperProps } from "./filterSelectorItemWrapper";

const { trackEvent } = useTracking();
const savedSearchesStore = useSavedSearchesStore();
const { t } = useI18n();

const emit = defineEmits<{
  "option-selected": [string];
  open: [];
  close: [];
  reset: [];
}>();

// Keep model optional for all callers that don't use search.
const queryModel = defineModel<string>("query", { default: "" });

const { label, options, hasSearch, variant } = defineProps<
  FilterSelectorItemWrapperProps & {
    label: string;
    options: UiMenuOptionType<string>[];
    hasSearch?: boolean;
  }
>();

const buttonAppearance = computed(() =>
  options.some((o) => o.selected) ? "fill-emphasis" : "outline-subtle",
);

const handleOpen = () => {
  trackEvent("filter_click_button_openFilterMenu", label);
  emit("open");
};

const handleClose = () => {
  emit("close");
};

const handleReset = () => {
  queryModel.value = "";
  emit("reset");
};

const onOptionSelected = (option: UiMenuSelectedOption<string>) => {
  savedSearchesStore.resetActiveSavedSearchId();

  const current = options.find((o) => o.value === option.value);

  trackEvent(
    "filter_click_checkbox_selectFilter",
    `${option.label} / ${option.label} / ${current?.selected}`,
  );

  emit("option-selected", option.value);
};
</script>

<style scoped>
:deep([data-side]) {
  z-index: 49;
}
</style>
